import { Card, CardContent, CardHeader, Divider, Grid, TextField } from "@mui/material";
import { ListItem, ListItemText } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { PasswordConfirmationDialog } from "../dialogs/PasswordConfirmationDialog";
import { CompanySetting, restoreCompanySettings, updateCompanySettings } from "../companySettingsAPI";
import { useAppDispatch } from "../../../../app/hooks";
import { showSuccess, showError } from "../../../notifications/notificationsSlice";
import { CardActionButtons } from "./CardActionButtons";
import { Localized } from "@fluent/react";
import { Help } from "../../../dialogs/HelpDialog";
import { RestoreSettingsPasswordConfirmationDialog } from "../dialogs/RestoreSettingsPasswordConfirmationDialog";

interface CompanyRiskAnalysisSettingCardProps {
    companySettings: CompanySetting[];
    onUpdate: (updatedSettings: CompanySetting[]) => void;
}

export const CompanyRiskAnalysisSettingCard: React.FC<CompanyRiskAnalysisSettingCardProps> = ({
    companySettings,
    onUpdate,
}) => {
    const dispatch = useAppDispatch();

    const [isEditing, setIsEditing] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [serverError, setServerError] = useState("");

    const goodSetting = useMemo(
        () => companySettings.find((s) => s.code === "GOOD_THRESHOLD"),
        [companySettings]
    );
    const warningSetting = useMemo(
        () => companySettings.find((s) => s.code === "WARNING_THRESHOLD"),
        [companySettings]
    );

    const [goodVal, setGoodVal] = useState<string>("");
    const [warningVal, setWarningVal] = useState<string>("");

    useEffect(() => {
        setGoodVal(goodSetting?.value ?? "");
        setWarningVal(warningSetting?.value ?? "");
    }, [goodSetting?.value, warningSetting?.value]);

    const isDefault = goodSetting?.isDefault === true && warningSetting?.isDefault === true;

    const toIntOrNaN = (s: string) => {
        if (s.trim() === "") return NaN;
        const n = Number(s);
        return Number.isInteger(n) ? n : NaN;
    };

    const inRange0to1000 = (n: number) => n >= 0 && n <= 1000;

    const goodNum = toIntOrNaN(goodVal);
    const warningNum = toIntOrNaN(warningVal);

    const goodValid = !Number.isNaN(goodNum) && inRange0to1000(goodNum);
    const warningValid = !Number.isNaN(warningNum) && inRange0to1000(warningNum);

    const goodHasError = isEditing && !goodValid;
    const warningHasError = isEditing && !warningValid;
    const relationErrorActive = isEditing && goodValid && warningValid && !(warningNum > goodNum);

    const hasAnyError = goodHasError || warningHasError || relationErrorActive;

    const isDirty =
        goodVal.trim() !== (goodSetting?.value ?? "") ||
        warningVal.trim() !== (warningSetting?.value ?? "");

    const handleRestore = () => setRestoreDialogOpen(true);
    const handleSave = () => setDialogOpen(true);
    const handleClose = () => {
        setDialogOpen(false);
        setServerError("");
    };
    const handleCloseRestore = () => {
        setRestoreDialogOpen(false);
        setServerError("");
    };

    const handleConfirmRestore = async (password: string) => {
        if (!password) return;

        setSaving(true);
        setServerError("");
        try {
            const result = await dispatch(
                restoreCompanySettings({
                    password,
                    settings: ["GOOD_THRESHOLD", "WARNING_THRESHOLD"],
                })
            );

            if (restoreCompanySettings.fulfilled.match(result)) {
                const settings = result.payload as CompanySetting[];
                onUpdate(settings);
                setRestoreDialogOpen(false);
                setIsEditing(false);
                dispatch(showSuccess("saved"));
            } else {
                let errorMsg = "error";
                if (result.payload?.kind === "http" && result.payload.problem?.title) {
                    errorMsg = result.payload.problem.title;
                }
                setServerError(errorMsg);
            }
        } catch {
            dispatch(showError("error"));
        } finally {
            setSaving(false);
        }
    };

    const handleConfirmSave = async (password: string) => {
        if (!password) return;

        if (!goodSetting || !warningSetting) {
            setServerError("Brak wymaganych ustawień GOOD_THRESHOLD / WARNING_THRESHOLD.");
            return;
        }
        if (hasAnyError) return;
        if (!isDirty) {
            setDialogOpen(false);
            setIsEditing(false);
            return;
        }

        setSaving(true);
        setServerError("");

        try {
            const updatedSettings: CompanySetting[] = [
                { ...goodSetting, value: String(goodNum) },
                { ...warningSetting, value: String(warningNum) },
            ];

            const result = await dispatch(
                updateCompanySettings({
                    password,
                    settings: updatedSettings,
                })
            );

            if (updateCompanySettings.fulfilled.match(result)) {
                const settings = result.payload as CompanySetting[];
                onUpdate(settings);
                setDialogOpen(false);
                setIsEditing(false);
                dispatch(showSuccess("saved"));
            } else {
                let errorMsg = "error";
                if (result.payload?.kind === "http" && result.payload.problem?.title) {
                    errorMsg = result.payload.problem.title;
                }
                setServerError(errorMsg);
            }
        } catch {
            dispatch(showError("error"));
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = () => {
        setGoodVal(goodSetting?.value ?? "");
        setWarningVal(warningSetting?.value ?? "");
        setIsEditing(false);
        setServerError("");
    };

    const goodHelperText = goodHasError ? (
        <Localized id="company-settings-ra-range-validation">
            <span>_Podaj liczbę całkowitą w zakresie 0–1000</span>
        </Localized>
    ) : (
        <Localized id="company-settings-ra-good-value-info">
            <span>_Wartość dla poziomu „dobry” (liczba całkowita 0–1000)</span>
        </Localized>
    );

    const warningHelperText = warningHasError ? (
        <Localized id="company-settings-ra-range-validation">
            <span>_Podaj liczbę całkowitą w zakresie 0–1000</span>
        </Localized>
    ) : relationErrorActive ? (
        <Localized id="company-settings-ra-error-value-relation">
            <span>_„Warning” musi być większe niż „Good”</span>
        </Localized>
    ) : (
        <Localized id="company-settings-ra-warning-value-info">
            <span>_Wartość dla poziomu „ostrzeżenie” (0–1000, większa niż „Good”)</span>
        </Localized>
    );

    return (
        <Card sx={{ maxWidth: 500, borderRadius: 2, boxShadow: 3 }}>
            <CardHeader
                title={
                    <Localized id="company-settings-ra">
                        <span>_Ustawienia analizy ryzyka</span>
                    </Localized>
                }
                sx={{
                    textAlign: "center",
                    bgcolor: "primary.main",
                    color: "white",
                    py: 1,
                    "& .MuiCardHeader-title": { fontSize: "1rem" },
                }}
            />
            <CardContent>
                <ListItem sx={{ pb: 0 }}>
                    <ListItemText
                        primary={
                            <>
                                <Localized id="company-settings-ra-parameters">
                                    <span>_Parametry analizy ryzyka</span>
                                </Localized>
                                <Help id="07f3837d-0c90-4214-8d9a-f874c64af48a" />
                            </>
                        }
                    />
                </ListItem>

                <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            type="number"
                            label={
                                <Localized id="company-settings-ra-good-value">
                                    <span>_Limit „dobry”</span>
                                </Localized>
                            }
                            value={goodVal}
                            onChange={(e) => setGoodVal(e.target.value)}
                            disabled={!isEditing}
                            inputProps={{ step: 1, min: 0, max: 1000 }}
                            error={goodHasError}
                            helperText={goodHelperText}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            type="number"
                            label={
                                <Localized id="company-settings-ra-warning-value">
                                    <span>_limit warning threshold</span>
                                </Localized>
                            }
                            value={warningVal}
                            onChange={(e) => setWarningVal(e.target.value)}
                            disabled={!isEditing}
                            inputProps={{ step: 1, min: 0, max: 1000 }}
                            error={warningHasError || relationErrorActive}
                            helperText={warningHelperText}
                        />
                    </Grid>
                </Grid>

                <Divider sx={{ width: "100%", mt: 3 }} />

                <CardActionButtons
                    isDefault={isDefault}
                    isEditing={isEditing}
                    onRestore={handleRestore}
                    onSave={handleSave}
                    onCancel={handleCancel}
                    onEdit={() => setIsEditing(true)}
                />
            </CardContent>

            {dialogOpen && (
                <PasswordConfirmationDialog
                    saving={saving}
                    serverError={serverError}
                    onClose={handleClose}
                    onConfirm={handleConfirmSave}
                />
            )}

            {restoreDialogOpen && (
                <RestoreSettingsPasswordConfirmationDialog
                    saving={saving}
                    serverError={serverError}
                    onClose={handleCloseRestore}
                    onConfirm={handleConfirmRestore}
                />
            )}
        </Card>
    );
};
